import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useLazyQuery } from '@apollo/client';
import ShowToast, { ToastTypeEnum } from '../../../common/components/ShowToast';
import { fetchDiagramData, getAiOpportunityByDiagramId } from '../../../services/diagramService';
import { openDiagram } from '../store/diagramViewerSlice';
import { DiagramDataType } from '../store/diagramViewerInterface';
import { GET_SUBPROCESS_DATA } from '../../../graphql/queries/getTaxonomyData';
import type { RootState } from '../../../store';
import { AiOpportunity } from '../../NewSolutionXPLR/state';

interface UseDiagramDeepLinkLoaderParams {
  diagramData: DiagramDataType | null;
}

/**
 * Decodes a query param value. IDs in deep links are stored encoded;
 * decode once when reading from the URL.
 */
function decodeParam(value: string | null): string | null {
  if (value == null || value === '') return null;
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/**
 * Hook to support deep links like /diagram?diagramId=<id>&processId=<id>&organizationId=<id>.
 *
 * When the URL includes diagramId (and optionally processId, organizationId), and there is no
 * existing diagram data in Redux, this hook fetches the diagram XML, loads
 * opportunities, and initializes the global diagram viewer state.
 *
 * - If organizationId is present in the URL: the diagram is shown only if that
 *   organization is present in Redux (user has access). Otherwise a toast is shown
 *   and the diagram is not loaded.
 * - If processId is present: uses GraphQL GET_SUBPROCESS_DATA (same as ideation
 *   flow) to get diagram.xmlData, then fetchDiagramData(xmlUrl, diagramId).
 * - If processId is absent or GraphQL returns no xmlData: uses
 *   fetchDiagramData('', diagramId) which relies on the e2e_taxonomy fallback path.
 *
 * Query params are expected to be encoded when the link is built; this hook
 * decodes them when reading.
 *
 * Returns isLoadingDeepLink: true while fetchDiagramData (and related APIs) are
 * in progress, so the caller can show a loading UI.
 */
export const useDiagramDeepLinkLoader = ({
  diagramData,
}: UseDiagramDeepLinkLoaderParams): { isLoadingDeepLink: boolean } => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const organisations = useSelector((state: RootState) => state.organisation ?? []);
  const [fetchSubprocessData] = useLazyQuery(GET_SUBPROCESS_DATA);
  const [isLoadingDeepLink, setIsLoadingDeepLink] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const encodedDiagramId = searchParams.get('diagramId');
    const encodedProcessId = searchParams.get('processId');
    const encodedOrganizationId = searchParams.get('organizationId');

    const diagramIdFromUrl = decodeParam(encodedDiagramId);
    const processIdFromUrl = decodeParam(encodedProcessId);
    const organizationIdFromUrl = decodeParam(encodedOrganizationId);

    if (!diagramIdFromUrl) {
      return;
    }

    if (organizationIdFromUrl) {
      const hasAccessToOrg = organisations.some(
        (org: { id: string }) => org.id === organizationIdFromUrl
      );
      if (!hasAccessToOrg) {
        ShowToast({
          message: 'Diagram does not belong to this organization',
          type: ToastTypeEnum.Error,
        });
        navigate('/', { replace: true });
        return;
      }
    }

    // If diagram data is already loaded (e.g., navigated from inside app),
    // do not refetch it.
    if (diagramData?.DiagramData?.diagram?.id) {
      return;
    }

    setIsLoadingDeepLink(true);

    const loadDiagramForDeepLink = async () => {
      try {
        let xmlUrl = '';
        let diagramDataFromTaxonomy: {
          data?: Record<string, unknown>;
          name?: string;
          files?: string[];
          kpis?: unknown;
        } = {};

        if (processIdFromUrl) {
          try {
            const taxonomyResponse = await fetchSubprocessData({
              variables: { filters: { id: processIdFromUrl } },
            });
            const get = (
              taxonomyResponse.data as { taxonomySubProcess?: { get?: Record<string, unknown> } }
            )?.taxonomySubProcess?.get;
            const diagram = get?.diagram as
              | { id?: string; xmlData?: string; data?: Record<string, unknown> }
              | undefined;
            xmlUrl = diagram?.xmlData ?? '';
            if (get) {
              diagramDataFromTaxonomy = {
                data: diagram?.data,
                name: get.name as string,
                files: get.files as string[],
                kpis: get.kpis,
              };
            }
          } catch {
            // Fall back to diagramId-only path
          }
        }

        const diagramXmlResponse = await fetchDiagramData(xmlUrl, diagramIdFromUrl || '');
        const emptyDeclaration = '<?xml version="1.0" encoding="UTF-8"?>';
        const xmlContent = diagramXmlResponse?.xml?.toString?.() ?? '';
        if (!xmlContent || xmlContent.trim() === emptyDeclaration.trim()) {
          ShowToast({
            message: 'No diagram found for this link',
            type: ToastTypeEnum.Error,
          });
          return;
        }

        let aiOpportunityData: AiOpportunity[] = [];
        try {
          const opportunitiesResponse = await getAiOpportunityByDiagramId(diagramIdFromUrl || '');
          aiOpportunityData = Array.isArray(opportunitiesResponse)
            ? opportunitiesResponse
            : ((opportunitiesResponse as { opportunities?: AiOpportunity[] })?.opportunities ?? []);
        } catch {
          aiOpportunityData = [];
        }

        const xmlString = diagramXmlResponse.xml.toString();
        const processedDiagramData = {
          diagram: {
            id: (diagramIdFromUrl || '') as string,
            xml: xmlString,
            data: (diagramDataFromTaxonomy.data ?? {}) as {
              [key: string]: { name: string; description: string };
            },
          },
        };

        const cardData = {
          name: diagramDataFromTaxonomy.name ?? 'Process Diagram',
          id: processIdFromUrl ?? diagramIdFromUrl,
          processType: 'custom' as const,
          diagramId: diagramIdFromUrl,
          useCaseId: processIdFromUrl ?? diagramIdFromUrl,
          usecase_id: processIdFromUrl ?? diagramIdFromUrl,
          files: (diagramDataFromTaxonomy.files ?? []) as unknown as Record<string, unknown>,
          kpis: (diagramDataFromTaxonomy.kpis ?? {}) as Record<string, unknown>,
          opportunity: { items: [] as unknown[] },
          taxonomy: {
            process: diagramDataFromTaxonomy.name ?? 'Process Diagram',
            domain: '',
            function: '',
            process_group: '',
          },
        };

        dispatch(
          openDiagram({
            DiagramData: processedDiagramData,
            aiOpportunityData,
            selectedTaxonomy: undefined,
            selectedE2E: undefined,
            selectedSubprocess: undefined,
            taxonomySubprocessName: diagramDataFromTaxonomy.name ?? 'Process Diagram',
            taxonomySubprocessDescription: '',
            selectedCard: cardData,
            useCaseData: undefined,
            useCaseDiagramDetails: processedDiagramData,
          })
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading diagram from deep link:', error);
        ShowToast({
          message: 'Failed to load diagram from link',
          type: ToastTypeEnum.Error,
        });
      } finally {
        setIsLoadingDeepLink(false);
      }
    };

    void loadDiagramForDeepLink();
  }, [searchParams, diagramData, dispatch, fetchSubprocessData, organisations]);

  return { isLoadingDeepLink };
};
